import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch } from '../../../store/store';
import { showSnackbar } from '../../../store/reducers/snackbarSlice';
import { Company } from '../../../types';
import ConfirmModal from '../../ConfirmationModal/ConfirmationModal';
import CompanyCallsTab from '../CompanyCallsTab/CompanyCallsTab';
import CompanyOrdersTab from '../CompanyOrdersTab/CompanyOrdersTab';
import CompanyEmailHistoryModal from '../CompanyEmailHistoryModal/CompanyEmailHistoryModal';
import CompanyLocationsModal from '../CompanyLocationsModal/CompanyLocationsModal';
import ModalWindowEmployees from '../ModalWindowEmployees/ModalWindowEmployees';
import SuccessModal from '../SuccessModal/SuccessModal';
import TabletQrModal from '../TabletQrModal/TabletQrModal';
import WidgetQrModal from '../WidgetQrModal/WidgetQrModal';
import CompanyForm from '../СompanyForm/CompanyForm';
import {
  getCompanyById,
  handleConfirmDelete,
  resendCompanyPasswordAndQr,
} from '../companyHelpers';
import style from './CompanyPage.module.css';

/* §FB37/§FB38: сторінка компанії з вкладками — замість каскаду модалок. Кожна вкладка
   рендерить наявний самодостатній компонент у embedded-режимі (дані він тягне за companyId
   сам), тож бізнес-логіка не дублюється. Активна вкладка живе в URL
   (/company/:companyId/:tab) — працюють deep-link, F5 і переходи з інших сторінок. */

const TABS = [
  { key: 'info', label: 'Дані компанії' },
  { key: 'employees', label: 'Співробітники' },
  { key: 'locations', label: 'Локації' },
  { key: 'calls', label: 'Дзвінки' },
  { key: 'orders', label: 'Заявки' },
  { key: 'widget', label: 'QR для віджету' },
  { key: 'nfc', label: 'QR/NFC-таблички' },
  { key: 'emails', label: 'Історія листів' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const CompanyPage: React.FC = () => {
  const { companyId, tab } = useParams<{ companyId: string; tab?: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [company, setCompany] = useState<Company | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  // §FB38: після «Надіслати запрошення» перемонтовуємо вкладку «Історія листів»,
  // щоб щойно надісланий лист одразу з'явився в списку.
  const [refreshKey, setRefreshKey] = useState(0);
  // §FB39: стан форми «Дані компанії» — «Зберегти» в хедері активна лише при змінах.
  const [infoForm, setInfoForm] = useState({ dirty: false, valid: false });
  const activeTabRef = useRef<HTMLButtonElement | null>(null);

  const activeTab: TabKey = TABS.some((t) => t.key === tab)
    ? (tab as TabKey)
    : 'info';

  // §FB38: ефект ключується ЛИШЕ на companyId (navigate/dispatch у deps робили
  // повне перезавантаження сторінки на кожен клік вкладки — нестабільний useNavigate).
  useEffect(() => {
    if (!companyId) return undefined;
    let active = true;
    (async () => {
      try {
        const loaded = await getCompanyById(companyId);
        if (!active) return;
        if (!loaded || !loaded.id) {
          // Порожня відповідь на валідний GUID — трактуємо як «не знайдено».
          setLoadFailed(true);
          return;
        }
        setCompany(loaded);
      } catch {
        if (active) setLoadFailed(true);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Невдале завантаження: тост + повернення до списку (окремим ефектом, щоб
  // не тягнути navigate/dispatch у залежності завантаження).
  useEffect(() => {
    if (!loadFailed) return;
    dispatch(
      showSnackbar({
        message: 'Не вдалося завантажити компанію',
        type: 'error',
      }),
    );
    navigate('/company');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFailed]);

  // §FB38: невідомий сегмент вкладки нормалізуємо в URL (щоб не поширювались биті лінки).
  useEffect(() => {
    if (tab && !TABS.some((t) => t.key === tab)) {
      navigate(`/company/${companyId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, companyId]);

  // §FB38: перемикання вкладок — звичайна push-навігація: «Назад» браузера
  // повертає по відвіданих вкладках, як і очікується від URL-вкладок.
  const openTab = (key: TabKey) => {
    navigate(`/company/${companyId}${key === 'info' ? '' : `/${key}`}`);
  };

  // Клавіатурна навігація вкладками (стрілки вліво/вправо), як у нативних tablist.
  const onTabKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const delta = e.key === 'ArrowRight' ? 1 : -1;
    const next = TABS[(index + delta + TABS.length) % TABS.length];
    openTab(next.key);
  };

  useEffect(() => {
    activeTabRef.current?.scrollIntoView({
      inline: 'nearest',
      block: 'nearest',
    });
  }, [activeTab]);

  const reloadCompany = async () => {
    if (!companyId) return;
    try {
      const loaded = await getCompanyById(companyId);
      if (loaded && loaded.id) setCompany(loaded);
    } catch {
      // Залишаємо наявні дані — форма вже показала свій тост про результат збереження.
    }
  };

  // Термін договору — та сама семантика, що в списку компаній: без дати — «Безстроковий»,
  // червоним — прострочений АБО менш як 30 днів до кінця.
  const now = new Date();
  const finishAt = company?.contractFinishAt
    ? new Date(company.contractFinishAt)
    : null;
  const daysLeft = finishAt
    ? Math.ceil((finishAt.getTime() - now.getTime()) / 86400000)
    : null;
  const termDanger = daysLeft !== null && daysLeft < 30;

  const formatDate = (value?: string) => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('uk-UA');
  };

  // §FB38: модалка успіху — ЛИШЕ після успішної відповіді API; помилка — тостом.
  const handleSendInvitation = async () => {
    if (!company || sendingInvite) return;
    setSendingInvite(true);
    try {
      await resendCompanyPasswordAndQr(company.id);
      setSuccessEmail(company.email);
      setRefreshKey((k) => k + 1);
    } catch {
      dispatch(
        showSnackbar({
          message: 'Не вдалося надіслати запрошення — спробуйте ще раз',
          type: 'error',
        }),
      );
    } finally {
      setSendingInvite(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!company) return;
    try {
      await handleConfirmDelete(
        company.id,
        () => navigate('/company'),
        setShowConfirmDelete,
      );
      dispatch(showSnackbar({ message: 'Компанію видалено', type: 'success' }));
    } catch {
      dispatch(
        showSnackbar({
          message: 'Не вдалося видалити компанію — спробуйте ще раз',
          type: 'error',
        }),
      );
    }
  };

  if (!company) {
    return (
      <div className={style.pageLoading}>
        <span className={style.spinner} aria-hidden="true" />
        Завантаження компанії…
      </div>
    );
  }

  return (
    <div className={style.page}>
      <div className={style.pageHeader}>
        <button
          type="button"
          className={style.backButton}
          onClick={() => navigate('/company')}
          title="Повернутися до списку компаній"
          aria-label="Повернутися до списку компаній"
        >
          ←
        </button>
        <div className={style.titleWrap}>
          <h4 className={style.title}>{company.name}</h4>
          <div className={style.badges}>
            {company.edrpou && (
              <span className={style.badge}>ЄДРПОУ {company.edrpou}</span>
            )}
            {company.plan && (
              <span className={style.badgePlan}>{company.plan}</span>
            )}
            {company.contractNumber && (
              <span className={style.badge}>
                Договір {company.contractNumber}
              </span>
            )}
            {finishAt ? (
              <span
                className={termDanger ? style.badgeExpired : style.badge}
                title={
                  daysLeft !== null && daysLeft < 0
                    ? 'Термін дії договору закінчився'
                    : daysLeft !== null && daysLeft < 30
                      ? `Договір закінчується через ${daysLeft} дн.`
                      : 'Договір чинний'
                }
              >
                до {formatDate(company.contractFinishAt)}
              </span>
            ) : (
              company.contractNumber && (
                <span
                  className={style.badge}
                  title="Дата завершення не вказана"
                >
                  Безстроковий
                </span>
              )
            )}
          </div>
        </div>
        <div className={style.headerActions}>
          {activeTab === 'info' && (
            <>
              <button
                type="button"
                className={style.deleteButtonHdr}
                onClick={() => setShowConfirmDelete(true)}
              >
                Видалити
              </button>
              <button
                type="submit"
                form="company-info-form"
                className={
                  infoForm.dirty && infoForm.valid
                    ? style.saveButtonActive
                    : style.saveButton
                }
                disabled={!infoForm.dirty || !infoForm.valid}
                title={
                  infoForm.dirty
                    ? 'Зберегти зміни'
                    : 'Немає незбережених змін'
                }
              >
                Зберегти
              </button>
            </>
          )}
          <button
            type="button"
            className={style.inviteButton}
            onClick={handleSendInvitation}
            disabled={sendingInvite}
          >
            {sendingInvite ? 'Надсилаємо…' : 'Надіслати запрошення'}
          </button>
        </div>
      </div>

      <div className={style.tabs} role="tablist" aria-label="Розділи компанії">
        {TABS.map((t, index) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeTab === t.key}
            tabIndex={activeTab === t.key ? 0 : -1}
            ref={activeTab === t.key ? activeTabRef : undefined}
            className={activeTab === t.key ? style.tabActive : style.tab}
            onClick={() => openTab(t.key)}
            onKeyDown={(e) => onTabKeyDown(e, index)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className={style.tabContent} role="tabpanel">
        {activeTab === 'info' && (
          <CompanyForm
            embedded
            isEdit
            companyData={company}
            onClose={() => undefined}
            onSave={() => reloadCompany()}
            onDelete={() => setShowConfirmDelete(true)}
            onFormStateChange={setInfoForm}
          />
        )}
        {activeTab === 'employees' && (
          <ModalWindowEmployees
            embedded
            selectedClientId={company.id}
            selectedClientName={company.name}
            onClose={() => undefined}
            onOpen={() => undefined}
          />
        )}
        {activeTab === 'locations' && (
          <CompanyLocationsModal
            embedded
            companyId={company.id}
            company={company}
            onClose={() => undefined}
          />
        )}
        {activeTab === 'widget' && (
          <WidgetQrModal
            embedded
            companyId={company.id}
            company={company}
            onClose={() => undefined}
          />
        )}
        {activeTab === 'nfc' && (
          <TabletQrModal
            embedded
            companyId={company.id}
            company={company}
            onClose={() => undefined}
          />
        )}
        {activeTab === 'emails' && (
          <CompanyEmailHistoryModal
            key={refreshKey}
            embedded
            companyId={company.id}
            companyName={company.name}
            onClose={() => undefined}
          />
        )}
        {activeTab === 'calls' && <CompanyCallsTab companyId={company.id} />}
        {activeTab === 'orders' && (
          <CompanyOrdersTab companyId={company.id} />
        )}
      </div>

      {showConfirmDelete && (
        <ConfirmModal
          userName={company.name}
          avatarUrl={company.avatarUrl}
          type="Company"
          onCancel={() => setShowConfirmDelete(false)}
          onConfirm={handleDeleteConfirmed}
        />
      )}
      {successEmail && (
        <SuccessModal
          email={successEmail}
          onClose={() => setSuccessEmail(null)}
        />
      )}
    </div>
  );
};

export default CompanyPage;
